import { useRoute, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Building2, DollarSign, TrendingUp, AlertCircle,
  Home, Phone, Mail, User, Search, ChevronRight, CheckCircle2, Clock,
} from "lucide-react";
import { useState, useMemo } from "react";
import type { FinancialRecord } from "@shared/schema";

type AptSummary = {
  id: number;
  name: string;
  bedrooms: number | null;
  buildingId: number | null;
  building: { name: string; address: string } | null;
  totalRevenue: number;
  collected: number;
  outstanding: number;
  recordCount: number;
};

type PMProfileData = {
  user: { id: number; fullName: string | null; username: string; email: string | null; phone: string | null; role: string };
  apartments: AptSummary[];
  // financials returned by /api/admin/pm/:userId — array of FinancialRecord rows (BUG-T12)
  financials: FinancialRecord[];
  summary: { totalRevenue: number; collected: number; outstanding: number; apartmentCount: number };
};

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function AdminPMProfile() {
  const [, params] = useRoute("/admin/pm/:userId");
  const userId = params?.userId ? Number(params.userId) : null;
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<PMProfileData>({
    queryKey: ["/api/admin/pm", userId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/pm/${userId}`);
      return res.json();
    },
    enabled: !!userId,
  });

  const filtered = useMemo(() => {
    if (!data?.apartments) return [];
    const q = search.toLowerCase();
    if (!q) return data.apartments;
    return data.apartments.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.building?.name?.toLowerCase().includes(q) ||
      a.building?.address?.toLowerCase().includes(q)
    );
  }, [data?.apartments, search]);

  const user = data?.user;
  const summary = data?.summary;

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/admin/users">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="btn-back-users">
              <ArrowLeft className="w-4 h-4" />
            </button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-display">
              {isLoading ? "Loading…" : (user?.fullName || user?.username || "PM Profile")}
            </h1>
            <p className="text-sm text-muted-foreground">Property Manager Profile</p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-muted-foreground">PM not found</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="md:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" /> Contact Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-semibold text-lg">{user?.fullName || user?.username}</p>
                  {user?.email && (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-pm-email">
                      <Mail className="w-3.5 h-3.5" /> {user.email}
                    </p>
                  )}
                  {user?.phone && (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-pm-phone">
                      <Phone className="w-3.5 h-3.5" /> {user.phone}
                    </p>
                  )}
                  <Badge variant="outline" className="text-xs capitalize">{user?.role}</Badge>
                </CardContent>
              </Card>

              <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard
                  icon={<Home className="w-4 h-4 text-primary" />}
                  label="Properties"
                  value={String(summary?.apartmentCount ?? 0)}
                  sub="under management"
                />
                <StatCard
                  icon={<TrendingUp className="w-4 h-4 text-blue-500" />}
                  label="Total Revenue"
                  value={fmt(summary?.totalRevenue ?? 0)}
                  sub="all time"
                />
                <StatCard
                  icon={<CheckCircle2 className="w-4 h-4 text-green-500" />}
                  label="Collected"
                  value={fmt(summary?.collected ?? 0)}
                  sub="paid"
                />
                <StatCard
                  icon={<AlertCircle className="w-4 h-4 text-amber-500" />}
                  label="Outstanding"
                  value={fmt(summary?.outstanding ?? 0)}
                  sub="pending"
                />
              </div>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Properties
                    <Badge variant="secondary">{data.apartments.length}</Badge>
                  </CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Filter properties…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="pl-8 h-8 text-sm"
                      data-testid="input-pm-property-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filtered.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    {search ? "No properties match your search." : "No properties assigned."}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left font-medium text-muted-foreground px-4 py-2">Property</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden sm:table-cell">Building</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2">Revenue</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2 hidden md:table-cell">Collected</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2 hidden md:table-cell">Outstanding</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2 hidden lg:table-cell">Records</th>
                          <th className="w-8 px-4 py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filtered.map(apt => (
                          <tr key={apt.id} className="hover:bg-muted/30 transition-colors" data-testid={`row-apt-${apt.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                  <Home className="w-3.5 h-3.5 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium">{apt.name}</p>
                                  {apt.bedrooms != null && (
                                    <p className="text-xs text-muted-foreground">{apt.bedrooms}BR</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              {apt.building ? (
                                <div>
                                  <p className="font-medium">{apt.building.name}</p>
                                  <p className="text-xs text-muted-foreground">{apt.building.address}</p>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium tabular-nums">{fmt(apt.totalRevenue)}</td>
                            <td className="px-4 py-3 text-right hidden md:table-cell">
                              <span className="text-green-600 dark:text-green-400 tabular-nums">{fmt(apt.collected)}</span>
                            </td>
                            <td className="px-4 py-3 text-right hidden md:table-cell">
                              {apt.outstanding > 0 ? (
                                <span className="text-amber-600 dark:text-amber-400 tabular-nums">{fmt(apt.outstanding)}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right hidden lg:table-cell">
                              <Badge variant="outline" className="text-xs">{apt.recordCount}</Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Link href={`/admin/apartments/${apt.id}`}>
                                <button className="p-1 rounded hover:bg-muted transition-colors" data-testid={`btn-apt-detail-${apt.id}`}>
                                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                </button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Recent Financial Records
                  <Badge variant="secondary">{data.financials.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.financials.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">No financial records yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left font-medium text-muted-foreground px-4 py-2">Description</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden sm:table-cell">Type</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2">Amount</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden md:table-cell">Status</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden lg:table-cell">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.financials.slice(0, 100).map(f => (
                          <tr key={f.id} className="hover:bg-muted/30 transition-colors" data-testid={`row-financial-${f.id}`}>
                            <td className="px-4 py-2.5">
                              <p className="font-medium truncate max-w-[200px]">{f.description || "—"}</p>
                            </td>
                            <td className="px-4 py-2.5 hidden sm:table-cell">
                              <Badge variant="outline" className="text-xs capitalize">
                                {(f.type || "").replace(/_/g, " ")}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                              {fmt(parseFloat(f.totalAmount || "0"))}
                            </td>
                            <td className="px-4 py-2.5 hidden md:table-cell">
                              <Badge
                                variant={f.status === "paid" ? "default" : "secondary"}
                                className={`text-xs ${f.status === "paid" ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"}`}
                              >
                                {f.status === "paid" ? <CheckCircle2 className="w-3 h-3 mr-1 inline" /> : <Clock className="w-3 h-3 mr-1 inline" />}
                                {f.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">
                              {f.createdAt ? new Date(f.createdAt).toLocaleDateString() : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {data.financials.length > 100 && (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        Showing 100 of {data.financials.length} records
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
        </div>
        <p className="text-xl font-bold tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}
